<template>
  <div class="basic_style_asset social-post w-100" :class="{ as_list: renderAsList }">
    <div class="wrapper no-footer">
      <div class="header">
        <div class="asset-type">
          <i class="bi bi-share" />
          <h6>Social Post</h6>
        </div>
      </div>
      <h2 v-if="showTitle && contentAsset.title">{{ contentAsset.title }}</h2>
      <div v-if="showDescription && contentAsset.description" class="description">
        {{ contentAsset.description }}
      </div>

      <div
        class="mt-3 position-relative"
        :data-testid="`social-post-${isLoading ? 'loading' : 'loaded'}`"
      >
        <div v-if="embedUrl">
          <div v-if="isLoading" class="loading-overlay">
            <ue-spinner />
          </div>

          <div v-if="isTwitter" v-show="!isLoading" class="w-100 d-flex justify-content-center">
            <div class="d-flex justify-content-center w-100">
              <!-- Use Twitter Widget API when enabled -->
              <div
                v-if="useTwitterWidgetApi"
                :ref="`twitter-widget-${contentAsset.id}`"
                class="twitter-widget-container"
                :style="{ height: twitterWidgetHeight ? `${twitterWidgetHeight}px` : 'auto' }"
              ></div>
              <!-- Use traditional iframe when Widget API is disabled -->
              <iframe
                v-else
                :src="embedUrl"
                class="twitter-iframe rounded border-0"
                allowfullscreen
                frameborder="0"
                allowtransparency="true"
                @load="handleIframeLoad"
              />
            </div>
          </div>
          <div
            v-else-if="isLinkedIn"
            v-show="!isLoading"
            class="w-100 d-flex justify-content-center"
          >
            <iframe
              :src="embedUrl"
              class="linkedin-iframe border-0"
              allowfullscreen
              frameborder="0"
              title="Embedded LinkedIn Post"
              @load="handleIframeLoad"
            />
          </div>
          <div
            v-else-if="isInstagram"
            v-show="!isLoading"
            class="w-100 d-flex justify-content-center"
          >
            <iframe
              :src="embedUrl"
              class="instagram-iframe rounded shadow border-0"
              frameborder="0"
              :scrolling="scrollable ? 'yes' : 'no'"
              allowtransparency="true"
              @load="handleIframeLoad"
            />
          </div>
          <div
            v-else-if="contentAsset.url && !isTwitter && !isLinkedIn && !isInstagram"
            class="text-center p-4 bg-light rounded error-message"
          >
            Invalid or unsupported social media URL
          </div>
        </div>
        <div
          v-else-if="!contentAsset.url"
          class="text-center p-4 bg-light rounded placeholder-message"
        >
          Enter a social media URL to see the preview
        </div>
      </div>
    </div>

    <BasicFooter
      v-if="embedUrl"
      :verifiedAt="contentAsset.verified_at"
      :ueId="contentAsset.identifier"
      surveySummary="Collected by UserEvidence"
    />
  </div>
</template>

<script lang="ts">
import BasicFooter from '@components/basic_asset_parts/BasicFooter.vue'
import AssetSourceMixin from '@app/mixins/asset_source'
import { isTwitter, isLinkedIn, isInstagram, getEmbedUrl } from '@lib/social_embed'

export default {
  name: 'SocialPost',
  components: { BasicFooter },
  mixins: [AssetSourceMixin],
  props: {
    contentAsset: { type: Object, required: true },
    account: { type: Object, required: true },
    // Toggle to check if the SocialPost should be rendered in a "List" context
    renderAsList: { type: Boolean, required: false, default: false },

    // Controls whether iframes should be scrollable
    scrollable: { type: Boolean, required: false, default: true },

    // Controls whether to use Twitter Widget API for dynamic height adjustment
    useTwitterWidgetApi: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      isLoading: true,
      iframeLoaded: false,
      twitterWidgetHeight: null,
    }
  },
  computed: {
    isTwitter() {
      return isTwitter(this.contentAsset.url)
    },
    isLinkedIn() {
      return isLinkedIn(this.contentAsset.url)
    },
    isInstagram() {
      return isInstagram(this.contentAsset.url)
    },
    embedUrl() {
      return getEmbedUrl(this.contentAsset.url)
    },
    showTitle() {
      return this.contentAsset.show_title
    },
    showDescription() {
      return this.contentAsset.show_description
    },
  },
  methods: {
    handleIframeLoad() {
      this.iframeLoaded = true
      this.isLoading = false
    },

    async loadTwitterWidget() {
      if (!this.useTwitterWidgetApi || !this.isTwitter) return

      // Extract tweet ID from URL
      const tweetId = this.extractTweetId(this.contentAsset.url)
      if (!tweetId) return

      // Load Twitter widgets script if not already loaded
      if (!window.twttr) {
        await this.loadTwitterScript()
      }

      // Create the widget
      const container = this.$refs[`twitter-widget-${this.contentAsset.id}`]
      if (container && window.twttr) {
        try {
          const widget = await window.twttr.widgets.createTweet(tweetId, container, {
            theme: 'light',
            width: 550,
            align: 'center',
          })

          if (widget) {
            // Get the actual height of the widget
            setTimeout(() => {
              const height = widget.offsetHeight
              if (height) {
                this.twitterWidgetHeight = height
              }
              this.isLoading = false
            }, 100)
          }
        } catch (error) {
          this.isLoading = false
        }
      }
    },

    extractTweetId(url) {
      const match = url.match(/\/status\/(\d+)/)
      return match ? match[1] : null
    },

    loadTwitterScript() {
      return new Promise((resolve, reject) => {
        if (window.twttr) {
          resolve(window.twttr)
          return
        }

        const script = document.createElement('script')
        script.src = 'https://platform.twitter.com/widgets.js'
        script.async = true
        script.onload = () => resolve(window.twttr)
        script.onerror = reject
        document.head.appendChild(script)
      })
    },
  },
  watch: {
    embedUrl() {
      this.isLoading = true
      this.iframeLoaded = false
      if (this.useTwitterWidgetApi && this.isTwitter) {
        this.$nextTick(() => {
          this.loadTwitterWidget()
        })
      }
    },
  },
  mounted() {
    if (this.useTwitterWidgetApi && this.isTwitter) {
      this.$nextTick(() => {
        this.loadTwitterWidget()
      })
    }
  },
}
</script>

<style lang="sass" scoped>
.twitter-iframe
  width: 100%
  height: 250px
  // Twitter/X embeds have an optimal width range, and when they're
  // stretched too wide, there can be awkward spacing or layout issues
  // Keep at max-width 600px
  max-width: 600px

.twitter-widget-container
  width: 100%
  max-width: 600px
  display: flex
  justify-content: center

.linkedin-iframe
  width: 100%

.instagram-iframe
  width: 100%

// List view heights (renderAsList = true)
.social-post.as_list
  .twitter-iframe
    height: 250px

  .linkedin-iframe
    height: 600px

  .instagram-iframe
    height: 550px

// Show page heights (renderAsList = false)
.social-post:not(.as_list)
  .linkedin-iframe
    height: 620px

  .instagram-iframe
    height: 820px

.loading-overlay
  position: absolute
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
  z-index: 10

.description
  color: rgba(108, 116, 125, 1)
  font-weight: 400
  font-size: 0.875em

.error-message
  color: rgba(108, 116, 125, 1)
  font-weight: 400
  font-size: 0.875em

.placeholder-message
  color: rgba(108, 116, 125, 1)
  font-weight: 400
  font-size: 0.875em
</style>
